import * as Api from '@ViewModels';
import { apiModelTypeToAutomationStepType } from '../../../../extViewmodels/sdk/Utils';
import { css } from 'aphrodite';
import * as React from 'react';
import { Flipped, Flipper, HandleEnterUpdateDelete } from 'react-flip-toolkit';
import { EditAutomationTemplateStepsContext } from '../../../../models/Automations';
import { EditAutomationStep } from '../../../containers/EditAutomationTemplate/presentation';
import { EditAutomationTemplateItemSeparator } from '../EditAutomationTemplateItemSeparator';
import { styleSheet } from './styles';

export const EditAutomationTemplateSteps = React.forwardRef<
	Flipper,
	{ children?: React.ReactChild; onStepSaved?: () => void }
>(function EditAutomationTemplateStepsBase(props, ref) {
	const { children, onStepSaved } = props;
	const context = React.useContext(EditAutomationTemplateStepsContext);

	const onHandleEnterUpdateDelete = React.useCallback<HandleEnterUpdateDelete>(
		({ hideEnteringElements, animateEnteringElements, animateExitingElements, animateFlippedElements }) => {
			// simulatneous animations
			hideEnteringElements();
			animateExitingElements();
			animateFlippedElements();
			animateEnteringElements();
		},
		[]
	);

	const onAnimateIn = React.useCallback((el: HTMLElement, index: number) => {
		requestAnimationFrame(() => {
			el.style.transition = `opacity 0ms`;
			el.style.opacity = `0`;
			requestAnimationFrame(() => {
				el.style.transition = `opacity ${250 + index * 100}ms ease`;
				el.style.opacity = `1`;
			});
		});
	}, []);

	const onAnimateOut = React.useCallback((el: HTMLElement, index: number, removeElement: () => void) => {
		requestAnimationFrame(() => {
			const time = 250 + index * 100;
			el.style.transition = `opacity ${time}ms ease`;
			el.style.opacity = `0`;
			setTimeout(removeElement, time);
		});
	}, []);

	const steps = context?.editorSteps?.steps;

	// Memoize flipKey to avoid recalculation on every render
	const flipKey = React.useMemo(() => steps?.map(x => x.uuid).join('') || '', [steps]);

	return (
		<>
			<Flipper flipKey={flipKey} handleEnterUpdateDelete={onHandleEnterUpdateDelete} ref={ref}>
				{steps?.map((x, i) => {
					// Convert API _type (e.g., "SwitchAutomationStep") to enum format (e.g., "Switch")
					const stepType = x.automationStep?._type
						? apiModelTypeToAutomationStepType(x.automationStep._type as Api.ApiModelType)
						: undefined;
					const isSwitchStep = stepType === Api.AutomationStepType.Switch;
					const automationTemplate = context?.editorSteps?.automationTemplate;
					const isSwitchStepChild =
						!isSwitchStep &&
						!!automationTemplate?.rootTemplate &&
						automationTemplate.rootTemplate !== automationTemplate;
					const addPadding = i !== 0 && i === steps.length - 1 && !x.automationStep;
					return (
						<Flipped flipId={x.uuid} key={x.uuid} onAppear={onAnimateIn} onExit={onAnimateOut}>
							<div className={addPadding ? css(styleSheet.lastStep) : null}>
								{isSwitchStepChild ? (
									<div className={css(styleSheet.stepSeparator)} />
								) : (
									<EditAutomationTemplateItemSeparator className={css(styleSheet.stepSeparator)} />
								)}
								<EditAutomationStep
									automationTemplate={automationTemplate}
									onStepChanged={onStepSaved}
									index={i}
									key={x.uuid}
									step={x}
									steps={steps}
								/>
							</div>
						</Flipped>
					);
				})}
			</Flipper>
			{children}
		</>
	);
});
